import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  TrendingUp, Users, Leaf, BookOpen, BarChart, MessageSquare,
  Building, Briefcase, Factory, GraduationCap, ArrowRight, Download
} from 'lucide-react';
import {
  heroData,
  challengeData,
  pillarsData,
  roleData,
  insightsData,
  partnersData
} from '../data/mock';
import { MemberSpotlight } from '../components/MemberSpotlight';

const iconMap = {
  TrendingUp,
  Users,
  Leaf,
  BookOpen,
  BarChart,
  MessageSquare,
  Building,
  Briefcase,
  Factory,
  GraduationCap
};

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroData.image}
            alt="India Development"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F]/95 via-[#1E3A5F]/85 to-[#E87722]/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in">
            {heroData.headline}
          </h1>
          <p className="text-lg md:text-xl text-gray-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            {heroData.subtext}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#E87722] hover:bg-[#E87722]/90 text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <Link to="/work">
                Explore Our Work
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#1E3A5F] transition-all duration-300 hover:scale-105"
            >
              <Link to="/engage">Contact Us</Link>
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-6">
              {challengeData.headline}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {challengeData.points.map((point, index) => (
              <Card
                key={index}
                className="border-t-4 border-[#E87722] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <CardHeader>
                  <CardTitle className="text-xl text-[#1E3A5F]">
                    {point.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              The CSED Approach
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three interconnected pillars driving systemic change
            </p>
          </div>

          <div className="space-y-16">
            {pillarsData.map((pillar, index) => {
              const Icon = iconMap[pillar.icon];
              return (
                <div
                  key={pillar.id}
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-8 items-center`}
                >
                  {/* Image */}
                  <div className="w-full lg:w-1/2">
                    <div className="relative overflow-hidden rounded-xl shadow-2xl group">
                      <img
                        src={pillar.image}
                        alt={pillar.title}
                        className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/80 to-transparent" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="p-2 bg-[#E87722] rounded-lg">
                            <Icon className="text-white" size={24} />
                          </div>
                          <h3 className="text-2xl font-bold text-white">
                            {pillar.title}
                          </h3>
                        </div>
                        <p className="text-[#E87722] font-semibold text-lg">
                          {pillar.subtitle}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full lg:w-1/2 space-y-4">
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {pillar.description}
                    </p>
                    <div className="space-y-3">
                      <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-[#E87722]">
                        <h4 className="font-semibold text-[#1E3A5F] mb-2">
                          Approach
                        </h4>
                        <p className="text-gray-600">{pillar.approach}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-[#1E3A5F]">
                        <h4 className="font-semibold text-[#1E3A5F] mb-2">
                          Outcome
                        </h4>
                        <p className="text-gray-600">{pillar.outcome}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Role Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {roleData.headline}
            </h2>
            <p className="text-lg text-gray-200 max-w-3xl mx-auto mb-4">
              {roleData.description}
            </p>
            <p className="text-xl font-semibold text-[#E87722] italic">
              "{roleData.tagline}"
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {roleData.functions.map((func, index) => {
              const Icon = iconMap[func.icon];
              return (
                <Card
                  key={index}
                  className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2"
                >
                  <CardHeader>
                    <div className="p-3 bg-[#E87722] rounded-lg w-fit mb-4">
                      <Icon className="text-white" size={28} />
                    </div>
                    <CardTitle className="text-xl text-white">
                      {func.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-200">{func.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Insights Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              {insightsData.headline}
            </h2>
            <p className="text-lg text-gray-600">
              Evidence-based research and policy insights
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {insightsData.publications.map((pub) => (
              <Card
                key={pub.id}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                <CardHeader>
                  <div className="text-sm text-[#E87722] font-semibold mb-2">
                    {pub.type}
                  </div>
                  <CardTitle className="text-lg text-[#1E3A5F] leading-snug">
                    {pub.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-500">
                    {pub.date}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col justify-between">
                  <p className="text-gray-600 mb-4">{pub.description}</p>
                  <Button
                    variant="outline"
                    className="w-full border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white transition-colors"
                  >
                    <Download size={16} className="mr-2" />
                    Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
            >
              <Link to="/research">
                View All Research
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              {partnersData.headline}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {partnersData.description}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {partnersData.partnerTypes.map((partner, index) => {
              const Icon = iconMap[partner.icon];
              return (
                <Card
                  key={index}
                  className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-[#E87722]"
                >
                  <CardHeader>
                    <div className="mx-auto p-4 bg-[#E87722]/10 rounded-full w-fit mb-4">
                      <Icon className="text-[#E87722]" size={32} />
                    </div>
                    <CardTitle className="text-lg text-[#1E3A5F]">
                      {partner.type}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{partner.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Member Spotlight (renders only when enabled by admin) */}
      <MemberSpotlight />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1E3A5F] via-[#1E3A5F] to-[#E87722]/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Collaborate with CSED to Build Resilient Systems
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Partner with us to strengthen India's development architecture through
            evidence-based, systems-level interventions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#E87722] hover:bg-[#E87722]/90 text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <Link to="/engage">
                Contact Us
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#1E3A5F]"
            >
              <Link to="/about">Learn More About CSED</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
